/**
 * Auth Store - Zustand store for authentication state
 * Manages JWT tokens, login/logout, and session state
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { createJSONStorage } from "zustand/middleware";
import { jwtDecode } from "jwt-decode";
import { usePermissionStore } from "../stores/permissions.store";

interface JwtPayload {
  readonly id?: number;
  readonly roles?: string[];
  readonly exp?: number;
  readonly iat?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  qrCodeToken: string | null;
  isLoggedIn: boolean;
  roles: string[];
  userId: number | null;
}

export interface AuthActions {
  login: (token: string, refreshToken?: string) => void;
  loginQrCode: (token: string) => void;
  logout: () => void;
  logoutQrCode: () => void;
  setUserId: (userId: number) => void;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      qrCodeToken: null,
      isLoggedIn: false,
      roles: [],
      userId: null,

      login: (token, refreshToken = null) => {
        const decoded = jwtDecode<JwtPayload>(token);

        set({
          token,
          refreshToken,
          isLoggedIn: true,
          roles: decoded.roles || [],
          userId: decoded.id || null,
        });
      },

      loginQrCode: (token) => {
        set({ qrCodeToken: token });
      },

      logout: () => {
        set({
          token: null,
          refreshToken: null,
          qrCodeToken: null,
          isLoggedIn: false,
          roles: [],
          userId: null,
        });
        // Clear permissions on logout
        usePermissionStore.getState().clear();
      },

      logoutQrCode: () => {
        set({ qrCodeToken: null });
      },

      setUserId: (userId) => set({ userId }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Re-export for backward compatibility
export default useAuthStore;
